import { NestedValue } from 'react-hook-form';
import IDropdownReference from '../../utils/types/IDropdownReference';
import { IReview } from '../../utils/types/IReview';
import ICompany from '../../utils/types/ICompany';
import { PaginatedData } from '../../utils/types/PaginationTypes';
import { StatUser } from '../../utils/statApi/UsersApi';
import { IDocument } from '../../utils/statApi/DocumentApi';
import { IServiceContact } from '../companies/serviceContacts/interfaces';
import { ICompanyPickerTag } from '../companyGroups/detail/CompanyGroupCompanyPicker';

export interface IDormancyAssessmentRequest {
  id?: number;
  companyCode?: string;
  hasEmployees?: boolean;
  hasRevenue?: boolean;
  hasOperatingExpenses?: boolean;
  mercuryDocumentLink?: string;
  comments?: string;
}

export interface IEntityTerminationReviewApproval {
  reviewId: number;
  review: IReview;
  terminationLifecycleStatus?: IDropdownReference;
  legoCategory?: IDropdownReference;
}

export interface IBlockerReviewApproval {
  reviewId: number;
  review: IReview;
  terminationLifecycleStatus?: IDropdownReference;
}

export interface IFinanceIncompleteReviewApproval {
  reviewId: number;
  review: IReview;
  lifecycleStatus?: IDropdownReference;
  legalTerminationType?: ILegalTerminationType;
  hasMatchingLifecycleStatus?: boolean;
}

export interface IEntityTerminationReviewRejection {
  reviewId: number;
  review: IReview;
  lifecycleStatus?: IDropdownReference;
}

export interface ILegalTerminationType {
  id: number;
  fieldName: string;
}

export interface ILegalReviewApproval {
  reviewId: number;
  review: IReview;
  entityStatus?: IDropdownReference;
  legalStatus?: IDropdownReference;
  lifecycleStatus?: IDropdownReference;
  terminationLifecycleStatus?: IDropdownReference;
  legoCategory?: IDropdownReference;
  legalEffectiveDate?: Date;
  noticesPendingDate?: string;
  notificationDate?: string;
  legalTerminationType?: ILegalTerminationType;
}

export interface IAGMStatusSummaryData {
  agmStatus: string;
  processCount: number;
}

export interface IDormancyAssessmentData {
  id: number | null;
  employees: boolean | null;
  revenue: boolean | null;
  operatingExpenses: boolean | null;
  comments: string | null;
  documentName: string | null;
  documentUrl: string | null;
}

export interface ICompanyLegalStructure {
  directParentCompany: ICompany;
  directParentOwnershipPercentage: number;
  childrenCompanies: ICompanySubsidiary[];
  terminatedSubsidiariesCount: number;
  nonTerminatedSubsidiariesCount: number;
  noticesPendingSubsidiariesCount: number;
  activeSubsidiariesCount: number;
}

export interface ICompanySubsidiary {
  companyCode: string;
  entityLegalName: string;
  physicalCountryCode: string;
  directParentCompany: ICompany;
  directParentOwnershipPercentage: number;
}

export enum EntityTerminationStage {
  INTAKE = 'Intake',
  ACTIVE_INTEGRATING = 'Active Integrating',
  ACTIVE_WIND_DOWN = 'Active Wind Down',
  ELIGIBILITY_REVIEW = 'Eligibility Review',
  BLOCKER = 'Blocker',
  LEGAL_IN_PROCESS = 'Legal In Process',
  FINANCE_INCOMPLETE = 'Finance Incomplete',
  TERMINATION_COMPLETE = 'Termination Complete',
  ELIGIBLE = 'Eligible',
}

export enum TerminationLifecycleStatus {
  ACTIVE_INTEGRATING = 'Active Integrating',
  ACTIVE_WIND_DOWN = 'Active Wind Down',
  WAITING_FOR_TERM_REVIEW = 'Waiting for Term Review (Under Eligibility Review)',
  BLOCKER = 'Blocker',
  ELIGIBLE = 'Eligible',
  IN_PROCESS = 'In Process',
  COMPLETED = 'Completed',
}

export interface IMonitoringQueueSearchState {
  companyCodes: string[];
  countryCodes: string[];
  activeRequestsOnly: boolean;
  entityTerminationStage: string;
  statControllerIds: string[];
  terminationContactIds: string[];
  celaTermContactIds: string[];
  businessStakeholderContactIds: string[];
  anticipatedTerminationFiscalYear: string[];
  entityStatusIds: number[];
  terminationLifecycleStatusIds: number[];
  legalInProcessFollowUpRequired: boolean;
  taskOwnerIds: string[];
  taskAssigneeIds: string[];
  primaryBlockerCategoryIds: number[];
}

export interface TerminationStagesCounts {
  activeIntegratingCount: number;
  activeWindDownCount: number;
  intakeCount: number;
  eligibilityReviewCount: number;
  blockerCount: number;
  legalInProcessCount: number;
  financeIncompleteCount: number;
  eligibleCount: number;
  totalCount: number;
}

export interface MonitoringQueueListResults {
  id: number;
  companyCode: string;
  entityLegalName: string;
  physicalCountry: string;
  reportingGroup: string;
  intakeMethod: string;
  entityTerminationStage: string;
  financeIncompletePercentComplete?: number | null;
  sapDeactivationDate?: string | null;
}

export interface MonitoringQueueQueryResult {
  monitoringQueueList: PaginatedData<MonitoringQueueListResults>;
  entityTerminationStageTotals?: TerminationStagesCounts;
}

export enum FinanceIncompleteActionItemTemplate {
  StatToolOpenProcesses = 'Stat Tool Open Processes',
  SapDeactivation = 'SAP Deactivation',
}

export interface IFinanceIncompleteActionItemProgress {
  actionItemId: number;
  description: string;
  completedTaskCount: number;
  totalTaskCount: number;
  percentComplete?: number | null;
}

export interface IFinanceIncompleteProgress {
  completedTaskCount: number;
  totalTaskCount: number;
  percentComplete?: number | null;
  sapDeactivationDate?: string | null;
  actionItemsProgress?: IFinanceIncompleteActionItemProgress[];
}

export interface IFinanceIncompleteDetails {
  companyCode: string;
  actionItems: IEntityTerminationActionItem[];
  progress: IFinanceIncompleteProgress;
}

export enum LegalInProcessTaskActionType {
  CreatedTask = 'CREATEDTASK',
  CompletedTask = 'COMPLETEDTASK',
  UpdatedOwner = 'UPDATEDOWNER',
  UpdatedAssignee = 'UPDATEDASSIGNEE',
  PostedResponse = 'POSTEDRESPONSE',
  UpdatedComment = 'UPDATEDCOMMENT',
  UpdatedFollowUpFlagDate = 'UPDATEDFOLLOWUPFLAGDATE',
  UploadedDocument = 'UPLOADEDDOCUMENT',
  RemovedDocument = 'REMOVEDDOCUMENT',
}

export enum EntityTerminationUriSegment {
  EligibilityReview = 'eligibility-review',
  Blocker = 'blocker',
  LegalInProcess = 'legal-in-process',
  FinanceIncomplete = 'finance-incomplete',
  Overview = 'overview',
  MonitoringQueue = 'monitoring-queue',
}

export interface IEntityTerminationActionItem {
  id?: number;
  companyCode?: string;
  entityLegalName?: string;
  entityTerminationRequestId: number;
  legoCategory?: IDropdownReference;
  description: string;
  owner: IServiceContact;
  dateStarted?: Date | null;
  dateCompleted?: Date | null;
  status: string;
  isPrimaryBlocker?: boolean;
  tasks?: IEntityTerminationTask[];
}

export interface IEntityTerminationTask {
  id: string;
  description: string;
  comment: string;
  completedDate?: string;
  followUpFlagDate?: string;
  owner?: IServiceContact;
  assignee?: IServiceContact;
  assignees?: IServiceContact[];
  response?: string;
  isTemplateTask?: boolean;
  documents?: IDocument[];
  taskHistories: ILegalInProcessTaskUpdate[];
}

export interface IEntityTerminationIntakeRequest {
  company: NestedValue<ICompanyPickerTag[]>;
  businessReason: string;
  hasEmployees: boolean | null;
  hasRevenueContracts: boolean | null;
  hasOtherOperatingExpenses: boolean | null;
  businessSponsorApprovalDocuments: NestedValue<IDocument[]>;
}

export interface ILegalInProcessActionItemUpdate {
  id: number;
  legalInProcessActionItemId: string;
  actionType: LegalInProcessTaskActionType;
  actionValue: string;
  actionDate: Date;
  changedBy: string;
}

export interface ILegalInProcessTaskUpdate {
  id: number;
  legalInProcessActionItemTaskId: number;
  actionType: LegalInProcessTaskActionType;
  updatedValue: string;
  actionDate: Date;
  changedBy: StatUser;
}

export interface ILegalInProcessActionItemParams {
  companyCode: string;
  actionItemId: string;
}

export interface ILegalInProcessDetails {
  accountingPlanLink?: string;
}

export interface IBlockerDetails {
  lifecycleStatus?: IDropdownReference;
  terminationLifecycleStatus?: IDropdownReference;
}

export interface IEntityTerminationStagePivotItem {
  itemKey: string;
  urlSegment: string;
}

export interface IEntityTerminationStagePivotItems {
  overview: IEntityTerminationStagePivotItem;
  eligibilityReview: IEntityTerminationStagePivotItem;
  blocker: IEntityTerminationStagePivotItem;
  legalInProcess: IEntityTerminationStagePivotItem;
  financeIncomplete: IEntityTerminationStagePivotItem;
  eligible: IEntityTerminationStagePivotItem;
}

export interface IEntityTerminationStatusMapping {
  entityStatusId: number;
  entityStatusFieldName: string;
  lifecycleStatusId: number;
  lifecycleStatusFieldName: string;
  terminationLifecycleStatusId: number;
  terminationLifecycleStatusFieldName: string;
}
